import '../styles/globals.css';

import type { AppProps } from 'next/app';
import { useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { Head } from '@zg-rentals/next/app';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { useRouter } from 'next/router';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent } from '../analytics';
import { useSiftScience } from '@zg-rentals/sift-science';
import { useAuth } from '@/AuthProvider';
import { setClient as setAdManagementClient } from '@zg-rentals/ts-ad-management-client';
import { AppProviders } from '../app/providers';
import { httpClient } from '../app/providers/httpClient';
import { ErrorBoundary } from '../app/error-boundary';
import type { AdManagementNextPage } from '../types';

setAdManagementClient(httpClient);

type AppPropsWithLayout = AppProps & {
  Component: AdManagementNextPage;
};

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  useBootstrap(() => import('../bootstrap/browser'));

  const router = useRouter();
  const { user } = useAuth();

  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });
  useRegisterNotification({ ref: 'AD_MANAGEMENT_WEB' });

  const getLayout = Component.getLayout ?? ((page) => <>{page}</>);

  const title = Component.title || 'Ad Management';

  return (
    <>
      <Head applicationName="ad-management-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        <title>{title}</title>
      </Head>

      <ErrorBoundary>
        <AppProviders>{getLayout(<Component {...pageProps} />)}</AppProviders>
      </ErrorBoundary>
    </>
  );
};

export default MyApp;
